/**
 * ChangeSummaryPanel — the F8 revision summary (变更摘要面板).
 *
 * Renders the comparison-level bullet points. Three shapes:
 *   - never analyzed (generatedAt === null, no points)  → quiet prompt
 *   - analyzed, degraded (LLM unreachable)              → handled by the parent's
 *     degrade banner; here we just show the empty-summary note
 *   - analyzed with points                              → bullet list
 *
 * Each point carries optional `changeIds`; we surface the count as a quiet
 * affordance (full drill-down to specific changes is a later iteration).
 */
import * as React from 'react';
import type { ChangeSummary } from '../../lib/veridoc-api';

export function ChangeSummaryPanel({
  summary,
}: {
  summary: ChangeSummary | null;
}): React.JSX.Element {
  const points = summary?.points ?? [];
  const analyzed = summary?.generatedAt != null;
  const hasPoints = points.length > 0;

  return (
    <section className="rounded-lg border border-ink-200 bg-ink-0 px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-900">修订摘要</h3>
        {summary?.generatedAt && (
          <span className="text-xs text-ink-400">
            生成于 {formatTimestamp(summary.generatedAt)}
          </span>
        )}
      </div>

      {hasPoints ? (
        <ul className="mt-3 flex flex-col gap-2">
          {points.map((point, i) => (
            <li key={i} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500"
              />
              <div className="min-w-0">
                <p className="text-sm leading-relaxed text-ink-800">{point.text}</p>
                {point.changeIds.length > 0 && (
                  <span className="mt-0.5 inline-block text-xs text-ink-400">
                    关联 {point.changeIds.length} 处变更
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs leading-relaxed text-ink-500">
          {analyzed
            ? '本次修订未生成摘要要点（无实质变更或摘要降级）。'
            : '尚未生成修订摘要。发起变更分析后，这里会给出本次修订的要点概览。'}
        </p>
      )}
    </section>
  );
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  // Local, minute precision — the operator only needs "when", not millis.
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default ChangeSummaryPanel;
